import React, {useEffect, useLayoutEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom'; // Import useNavigate
import Spinner from 'react-bootstrap/Spinner';
import Button from 'react-bootstrap/Button';
import Table from 'react-bootstrap/Table';
import axios from 'axios';

import {showToast as toast} from '../toastManager';
import errorMessages from '../errorMessages.js';
import {v4 as uuidv4} from 'uuid'; // Import uuid for unique IDs

const CampaignSummary = ({ ViewedPage, shop_domain, app_url }) => {
    ViewedPage(28);
    const navigate = useNavigate(); // Logic Added: Initialize useNavigate

    const [loading, setLoading] = useState(true);
    const [campaigns, setCampaigns] = useState([]);
    const [spreadsheetUrl, setSpreadsheetUrl] = useState("");
    const [exportLoading, setExportLoading] = useState(false);

    // Reset state immediately when the selected store changes
    useLayoutEffect(() => {
        if (shop_domain) {
            setLoading(true);
            setCampaigns([]);
        } else {
            setLoading(false);
            setCampaigns([]);
        }
    }, [shop_domain]);

    // Fetch campaigns from API
    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const response = await axios.get(`${app_url}/api/store/personalized_campaigns`, {
                    withCredentials: true
                });

                if (response.data.success) {
                    setCampaigns(response.data.data.map(c => ({ ...c })));
                } else {
                    throw new Error(response.data.message || 'Failed to fetch campaigns');
                }
            } catch (error) {
                console.error('Error fetching campaigns:', error);
                toast.error(errorMessages["DATA_LOAD_ERROR"]["title"]);
            } finally {
                setLoading(false);
            }
        };

        if (shop_domain) {
            fetchCampaigns();
        } else {
            setLoading(false);
            setCampaigns([]);
        }
    }, [shop_domain, app_url]);

    // Save campaigns to API
    const saveCampaigns = async (updatedCampaigns) => {
        try {
            const response = await axios.put(`${app_url}/api/store/personalized_campaigns`,
                updatedCampaigns,
                { withCredentials: true }
            );

            if (response.data.success) {
                toast.success('Campaigns saved successfully');
            } else {
                throw new Error(response.data.message || 'Failed to save campaigns');
            }
        } catch (error) {
            console.error('Error saving campaigns:', error);
            toast.error(errorMessages["DATA_SAVE_ERROR"]["title"]);
        }
    };

    // Delete campaign
    const deleteCampaign = async (campaignId) => {
        try {
            const campaignToDelete = campaigns.find(c => c.campaign_id === campaignId);
            const variantKeys = [];
            (campaignToDelete?.targeting_and_content || []).forEach(tc => {
                (tc?.targeting || []).forEach(t => {
                    if (!t) return;
                    if (typeof t === 'string') variantKeys.push(t);
                    else if (t.label) variantKeys.push(t.label);
                    else if (t.value) variantKeys.push(t.value);
                });
            });

            const cleanupPayload = {
                campaign_id: campaignId,
                variant_keys: [...new Set(variantKeys.filter(Boolean))],
                remove_campaign: true
            };

            const response = await axios.post(`${app_url}/api/cux/delete-campaign`,
                cleanupPayload,
                { withCredentials: true }
            );

            if (response.data?.success) {
                const updatedCampaigns = response.data?.data || campaigns.filter(c => c.campaign_id !== campaignId);
                setCampaigns(updatedCampaigns);
                toast.success('Campaign deleted successfully');
            } else {
                throw new Error(response.data?.message || 'Failed to delete campaign');
            }
        } catch (error) {
            console.error('Error deleting campaign:', error);
            toast.error(errorMessages["DATA_DELETE_ERROR"]["title"]);
        }
    };

    const handleEditCampaign = (campaign) => {
        const targetingPerLandingPage = campaigns
            .filter(c => c.targeting_and_content && Array.isArray(c.targeting_and_content) && c.landing_page_urls)
            .reduce((acc, c) => {
                const url = c.landing_page_urls.trim();
                const isKeyword = c.category === 'Keyword Personalization';
                const targetings = c.targeting_and_content
                    .filter(tc => tc && Array.isArray(tc.targeting))
                    .flatMap(tc => [...tc.targeting, tc.preview_targeting])
                    .filter(Boolean)
                    .map(iter => {
                        if (isKeyword) {
                            if (iter?.value) return get_keyword_targeting(iter);
                            if (typeof iter === 'string') return iter;
                            return null;
                        }
                        return iter?.label ? iter.label.toLowerCase() : iter.toLowerCase();
                    })
                    .filter(Boolean);
                if (!acc[url]) acc[url] = [];
                acc[url] = [...new Set([...acc[url], ...targetings])];
                return acc;
            }, {});

        navigate('/personalization-campaign', {
            state: {
                campaignData: { ...campaign },
                allTargeting: targetingPerLandingPage
            }
        });
    };

    const handleDeleteCampaign = async (campaignId) => {
        if (window.confirm('Are you sure you want to delete this campaign?')) {
            await deleteCampaign(campaignId);
        }
    };

    const handleCreateCampaign = () => {
        const targetingPerLandingPage = campaigns
            .filter(c => c.targeting_and_content && Array.isArray(c.targeting_and_content) && c.landing_page_urls)
            .reduce((acc, c) => {
                const url = c.landing_page_urls.trim();
                const isKeyword = c.category === 'Keyword Personalization';
                const targetings = c.targeting_and_content
                    .filter(tc => tc && Array.isArray(tc.targeting))
                    .flatMap(tc => [...tc.targeting, tc.preview_targeting])
                    .filter(Boolean)
                    .map(iter => {
                        if (isKeyword) {
                            if (iter?.value) return get_keyword_targeting(iter);
                            if (typeof iter === 'string') return iter;
                            return null;
                        }
                        return iter?.label ? iter.label.toLowerCase() : iter.toLowerCase();
                    })
                    .filter(Boolean);
                if (!acc[url]) acc[url] = [];
                acc[url] = [...new Set([...acc[url], ...targetings])];
                return acc;
            }, {});

        const newCampaign = {
            campaign_id: uuidv4(),
            campaign_name: `Untitled Campaign`,
            status: 'draft',
            category: null,
            landing_page_urls: "",
            templates: {},
            targeting_and_content: [],
        };
        navigate('/category', {
            state: {
                campaignData: newCampaign,
                "allTargeting": targetingPerLandingPage
            }
        });
    };

    // Add function to get spreadsheet URL
    const getSpreadsheetUrl = async () => {
        setExportLoading(true);
        try {
            const response = await axios.get(`${app_url}/api/store/landing_page_spreadsheet_url`, {
                params: {
                    shop_domain: shop_domain
                },
                withCredentials: true
            });

            if (response.data.success && response.data.data?.spreadsheetUrl) {
                setSpreadsheetUrl(response.data.data.spreadsheetUrl);
                window.open(response.data.data.spreadsheetUrl, '_blank');
            } else {
                throw new Error('Failed to get spreadsheet URL');
            }
        } catch (error) {
            console.error('Error getting spreadsheet URL:', error);
            toast.error('Failed to get spreadsheet URL');
        } finally {
            setExportLoading(false);
        }
    };

    // Add useEffect to fetch spreadsheet URL on component mount
    useEffect(() => {
        const fetchSpreadsheetUrl = async () => {
            try {
                const response = await axios.get(`${app_url}/api/store/landing_page_spreadsheet_url`, {
                    params: {
                        shop_domain: shop_domain
                    },
                    withCredentials: true
                });

                if (response.data.success && response.data.data?.spreadsheetUrl) {
                    setSpreadsheetUrl(response.data.data.spreadsheetUrl);
                }
            } catch (error) {
                console.error('Error fetching spreadsheet URL:', error);
            }
        };

        if (shop_domain) {
            fetchSpreadsheetUrl();
        }
    }, [shop_domain, app_url]);

    const getStatusBadge = (status) => {
        const statusValue = (status || '').toLowerCase();
        const modifier = statusValue === 'live' ? 'live' : statusValue === 'draft' ? 'draft' : 'default';
        const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A';
        return (
            <span className={`sxi-status-badge sxi-status-badge--${modifier}`}>
                <span className="sxi-status-badge__dot"></span>
                <span className="sxi-status-badge__text">{label}</span>
            </span>
        );
    };

    const safeCampaigns = Array.isArray(campaigns) ? campaigns : [];

    return (
        <div className='main-container' style={{ position: "relative" }}>
            {!shop_domain ?
                <div className='go_to_shopify_admin_div'>
                    <h5 style={{ display: 'inline-block' }}>!! Go to Shopify Admin and open our app !!</h5>
                </div>
                :
                <>
                    {loading ?
                        <div className="text-center mt-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                        </div>
                        :
                        <>
                            <div className='opportunities-header main-title mt-4 d-flex justify-content-between align-items-center'>
                                <h2>Campaign Summary</h2>
                                <div>
                                    {/* <Button
                                        className='sxi_primary_blue_btn me-2'
                                        onClick={getSpreadsheetUrl}
                                        disabled={exportLoading}
                                    >
                                        {exportLoading ? (
                                            <>
                                                <Spinner
                                                    as="span"
                                                    animation="border"
                                                    size="sm"
                                                    role="status"
                                                    aria-hidden="true"
                                                    className="me-2"
                                                />
                                                Exporting...
                                            </>
                                        ) : (
                                            'Export All Variant'
                                        )}
                                    </Button> */}
                                    <Button
                                        className='sxi_primary_blue_btn btn-height'
                                        onClick={handleCreateCampaign}
                                    >
                                        Create
                                    </Button>
                                </div>
                            </div>

                            {safeCampaigns.length > 0 ?
                                <div className='main-accordion-body mt-4'>
                                    <div className="opportunities-keywords-table border rounded bg-white opp-padding">
                                        <Table responsive className="mb-0">
                                            <thead>
                                                <tr>
                                                    <th className="channel_performance_header_cell">Campaign</th>
                                                    <th className="channel_performance_header_cell">Status</th>
                                                    <th className="channel_performance_header_cell">Category</th>
                                                    <th className="channel_performance_header_cell">Date Launched</th>
                                                    <th className="channel_performance_header_cell text-center">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {safeCampaigns.map((campaign, i) => {
                                                    if (!campaign || !campaign.campaign_id) return null;
                                                    return (
                                                        <tr
                                                            key={campaign.campaign_id}
                                                            className={i % 2 === 0 ? 'channel_performance_table_row' : 'channel_performance_old_table_row'}
                                                        >
                                                            <td className="channel_performance_table_cell">{campaign.campaign_name || 'Untitled Campaign'}</td>
                                                            <td className="channel_performance_table_cell">{getStatusBadge(campaign.status)}</td>
                                                            <td className="channel_performance_table_cell">{campaign.category || 'N/A'}</td>
                                                            <td className="channel_performance_table_cell">{campaign.created_at ? campaign.created_at.split("T")[0] : 'N/A'}</td>
                                                            <td className="channel_performance_table_cell text-center">
                                                                
                                                                <button
                                                                    className="btn btn-sm btn-link p-0 me-3"
                                                                    onClick={() => handleEditCampaign(campaign)}
                                                                    title="Edit"
                                                                    style={{ color: '#000' }}
                                                                >
                                                                    <svg width="30" height="30" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" class="sxi-edit-icon"><g clip-path="url(#clip0_edit_${item.id})"><path d="M19.1667 13.3333H15.6667C14.2666 13.3333 13.5666 13.3333 13.0318 13.6058C12.5614 13.8455 12.1789 14.2279 11.9392 14.6983C11.6667 15.2331 11.6667 15.9332 11.6667 17.3333V24.3333C11.6667 25.7335 11.6667 26.4335 11.9392 26.9683C12.1789 27.4387 12.5614 27.8212 13.0318 28.0608C13.5666 28.3333 14.2666 28.3333 15.6667 28.3333H22.6667C24.0669 28.3333 24.7669 28.3333 25.3017 28.0608C25.7721 27.8212 26.1546 27.4387 26.3943 26.9683C26.6667 26.4335 26.6667 25.7335 26.6667 24.3333V20.8333M16.6667 23.3333H18.0622C18.4698 23.3333 18.6737 23.3333 18.8655 23.2873C19.0355 23.2464 19.1981 23.1791 19.3472 23.0877C19.5154 22.9847 19.6595 22.8405 19.9478 22.5523L27.9167 14.5833C28.6071 13.893 28.6071 12.7737 27.9167 12.0833C27.2264 11.393 26.1071 11.393 25.4167 12.0833L17.4478 20.0523C17.1595 20.3405 17.0154 20.4847 16.9123 20.6528C16.8209 20.802 16.7536 20.9645 16.7128 21.1346C16.6667 21.3264 16.6667 21.5302 16.6667 21.9379V23.3333Z" stroke="#475467" stroke-width="1.67" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_edit_${item.id}"><rect width="20" height="20" fill="white" transform="translate(10 10)"/></clipPath></defs></svg>
                                                                </button>

                                                                <button
                                                                    className="btn btn-sm btn-link p-0"
                                                                    onClick={() => handleDeleteCampaign(campaign.campaign_id)}
                                                                    title="Delete"
                                                                    style={{ color: '#000' }}
                                                                >
                                                                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M23.3333 15V14.3334C23.3333 13.3999 23.3333 12.9332 23.1517 12.5767C22.9919 12.2631 22.7369 12.0081 22.4233 11.8483C22.0668 11.6667 21.6001 11.6667 20.6667 11.6667H19.3333C18.3999 11.6667 17.9332 11.6667 17.5767 11.8483C17.2631 12.0081 17.0081 12.2631 16.8483 12.5767C16.6667 12.9332 16.6667 13.3999 16.6667 14.3334V15M18.3333 19.5834V23.75M21.6667 19.5834V23.75M12.5 15H27.5M25.8333 15V24.3334C25.8333 25.7335 25.8333 26.4336 25.5608 26.9683C25.3212 27.4387 24.9387 27.8212 24.4683 28.0609C23.9335 28.3334 23.2335 28.3334 21.8333 28.3334H18.1667C16.7665 28.3334 16.0665 28.3334 15.5317 28.0609C15.0613 27.8212 14.6788 27.4387 14.4392 26.9683C14.1667 26.4336 14.1667 25.7335 14.1667 24.3334V15" stroke="#475467" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                                                                    </svg>
                                                                    {/* <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16">
                                                                        <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5Zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6Z"/>
                                                                        <path d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1ZM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118ZM2.5 3h11V2h-11v1Z"/>
                                                                    </svg> */}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                                :
                                <div style={{height:"50%", display:"flex", justifyContent:"center", alignItems:"center"}}>
                                    <h5>Create Campaign</h5>
                                </div>
                            }
                        </>
                    }
                </>
            }
        </div>
    );
};

export default CampaignSummary;

function get_keyword_targeting(keyword_dict){
    const kw = keyword_dict?.["value"]?.[0];
    if (!kw || !kw["campaign_id"]) return null;
    return kw["campaign_id"] + "-" + kw["ad_group_id"] + "-" + kw["keyword_text"] + "-" + getMatchTypeLetter(kw["match_type"]);
}

function getMatchTypeLetter(matchType) {
    switch(matchType?.toLowerCase()) {
        case 'exact':
            return 'e';
        case 'phrase':
            return 'p';
        case 'broad':
            return 'b';
        default:
            return matchType || '';
    }
}